import { ONLINE_WAIT_POLL_MS, ONLINE_WAIT_TIMEOUT_MS } from './config';

/**
 * The nearest thing a browser has to Android's `NetworkGate` (SPEC §9).
 *
 * ## Why a cycle needs one at all
 *
 * With no network, every probe rejects and the aggregator concludes that every
 * link in the list is dead — a full batch of false negatives, submitted the
 * moment the network comes back, indistinguishable from a real region-wide
 * outage. That is the single worst failure this app can have, and it is also the
 * easiest one to hit: someone walks into a lift.
 *
 * ## What `navigator.onLine` is actually worth
 *
 * Read it in one direction only. **`false` is trustworthy** — the OS has no
 * usable interface, and nothing is going to reach anything. `true` means only
 * that an interface exists; captive portals and dead uplinks both report `true`.
 * So this gate can stop a run that is certainly doomed, and cannot promise that
 * one it lets through will work.
 *
 * That is strictly weaker than Android, which probes real URLs before deciding.
 * The compensating evidence here is the config call: a cycle that could not
 * reach our own backend, on a device reporting offline, does not submit.
 */
export function isOnline(): boolean {
  try {
    // `!== false` rather than `=== true`: a browser that does not implement the
    // property at all leaves it `undefined`, and a missing signal is not
    // evidence of an outage.
    return navigator.onLine !== false;
  } catch {
    return true;
  }
}

/**
 * Waits until the device reports a network, or the budget runs out.
 *
 * Bounded rather than infinite. This app runs in the foreground with a person in
 * front of it; a wait with no end is a hang, and the cycle can simply be retried
 * on the next tick.
 *
 * Wakes on the `online` **event** as well as polling, so a device that reconnects
 * one second into the wait resumes then rather than at the next poll boundary.
 * The poll is kept as the backstop: the event does not fire for every way a
 * network can come back, and a gate that depended on it alone would sit out its
 * whole budget on the cases it misses.
 *
 * @returns whether it ever came online.
 */
export async function awaitOnline(
  timeoutMs: number = ONLINE_WAIT_TIMEOUT_MS,
  signal?: AbortSignal,
  pollMs: number = ONLINE_WAIT_POLL_MS,
): Promise<boolean> {
  if (isOnline()) return true;
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (signal?.aborted) return isOnline();
    // Never sleep past the deadline. A fixed poll would make a 30-second budget
    // take a full poll interval longer than asked, which on the short budgets
    // this app uses is most of the budget.
    await waitForOnline(Math.min(pollMs, deadline - Date.now()), signal);
    if (isOnline()) return true;
  }
  return isOnline();
}

/**
 * Resolves on the first of: the `online` event, `ms` elapsing, or `signal`
 * aborting. Every listener is removed on whichever happens first — a
 * `Promise.race` would leave the losers attached, and this runs in a loop.
 */
function waitForOnline(ms: number, signal?: AbortSignal): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    // Guarded: this module is unit-tested under Node, which has no `window`.
    const target: EventTarget | undefined =
      typeof globalThis.addEventListener === 'function' ? globalThis : undefined;
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      target?.removeEventListener('online', finish);
      signal?.removeEventListener('abort', finish);
      resolve();
    };
    const timer = setTimeout(finish, ms);
    target?.addEventListener('online', finish, { once: true });
    // Resolves rather than rejects on abort: every caller's next move is to
    // re-check the state itself, so a rejection would only be caught and
    // discarded one frame later.
    signal?.addEventListener('abort', finish, { once: true });
  });
}
